import customFonts, { FontWeight } from 'common-assets/assets/fonts/fonts';
import { fontSize as fontSizeMap, lineHeightToFontSize } from './constants';

export type FontSizeType = 'xs' | 's' | 'base' | 'm' | 'l' | 'xl' | 'xxl' | 'xxxl' | 'xxxlLg' | 'xxxxl' | 'subHeadline2';

export const handleFontSize =
    (size?: FontSizeType, {
      xs = fontSizeMap.xs,
      s = fontSizeMap.s,
      base = fontSizeMap.base,
      m = fontSizeMap.m,
      l = fontSizeMap.l,
      xl = fontSizeMap.xl,
      xxl = fontSizeMap.xxl,
      xxxl = fontSizeMap.xxxl,
      xxxlLg = fontSizeMap.xxxlLg,
      xxxxl = fontSizeMap.xxxxl,
      subHeadline2 = fontSizeMap.subHeadline2,
    } = {}): number => {
      const fontSizes = {
        xs,
        s,
        base,
        m,
        l,
        xl,
        xxl,
        xxxl,
        xxxlLg,
        xxxxl,
        subHeadline2,
      };
      return size ? fontSizes[size] : base;
    };

const pxToRem = (value: number) => `${value / fontSizeMap.base}rem`; //html font size = 16px
const responsiveLineHeight = (fontSize: number, lineHeight: number) => Math.round((lineHeight / fontSize + Number.EPSILON) * 100) / 100;

export const fontSizeToLineHeightMap: Record<FontSizeType, number> = {
  xs: lineHeightToFontSize.xs,
  s: lineHeightToFontSize.s,
  base: lineHeightToFontSize.base,
  m: lineHeightToFontSize.l,
  l: lineHeightToFontSize.l,
  xl: lineHeightToFontSize.xl,
  xxl: lineHeightToFontSize.xxl,
  xxxl: lineHeightToFontSize.xxxl,
  xxxlLg: lineHeightToFontSize.xxxlLg,
  xxxxl: lineHeightToFontSize.xxxxl,
  subHeadline2: lineHeightToFontSize.subHeadline2,
} as const;

export const mapFontSizeTypeToLineHeight = (fontSize?: FontSizeType): number => {
  return fontSize && fontSize in fontSizeToLineHeightMap
    ? fontSizeToLineHeightMap[fontSize]
    : fontSizeToLineHeightMap.base;
};
export const fontText = (
    fontStyle: 'normal' | 'italic',
    fontSize: number, //give me value in px without unit
    lineHeight: number, //give me value in px without unit
) => ` ${fontStyle} ${FontWeight.REGULAR} ${pxToRem(fontSize)}/${responsiveLineHeight(fontSize, lineHeight)} ${customFonts.IngMeWebRegular}`;

export const fontDisplay = (
    fontStyle: 'normal' | 'italic',
    fontSize: number, //give me value in px without unit
    lineHeight: number, //give me value in px without unit
) => ` ${fontStyle} ${FontWeight.BOLD} ${pxToRem(fontSize)}/${responsiveLineHeight(fontSize, lineHeight)} ${customFonts.IngMeWebBold}`;

export const fontDisplayExtraBold= (
  fontStyle: 'normal' | 'italic',
  fontSize: number, //give me value in px without unit
  lineHeight: number, //give me value in px without unit
) => ` ${fontStyle} ${FontWeight.BOLD} ${pxToRem(fontSize)}/${responsiveLineHeight(fontSize, lineHeight)} ${customFonts.IngDisplayBold}`;

export const hexToRgbA = (hex: string, opacity = 1) => {
  let c;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('');
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c = `0x${c.join('')}`;
    return `rgba(${[(Number(c) >> 16) & 255, (Number(c) >> 8) & 255, Number(c) & 255].join(',')},${opacity})`;
  }
  throw new Error('Bad Hex');
};

const rgbFromHexToRgbA = (hex: string): [number, number, number] => {
  const rgba = hexToRgbA(hex, 1);
  const match = rgba.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);

  if (!match) {
    throw new Error(`Unable to parse RGB channels for color: ${hex}`);
  }

  return [Number(match[1]), Number(match[2]), Number(match[3])];
};

export const blendHexWithOverlay = (baseColor: string, overlayColor: string, overlayOpacity: number): string => {
  const [baseR, baseG, baseB] = rgbFromHexToRgbA(baseColor);
  const [overlayR, overlayG, overlayB] = rgbFromHexToRgbA(overlayColor);

  const blendChannel = (base: number, overlay: number) =>
    Math.round((1 - overlayOpacity) * base + overlayOpacity * overlay);

  const toHex = (channel: number) => channel.toString(16).padStart(2, '0').toUpperCase();

  return `#${toHex(blendChannel(baseR, overlayR))}${toHex(blendChannel(baseG, overlayG))}${toHex(blendChannel(baseB, overlayB))}`;
};
